import { usePersistedState, useScrollMemory } from '../../hooks/usePersistedState';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Button,
  Table,
  Select,
  DatePicker,
  Space,
  Row,
  Col,
  Typography,
  Card,
  Popconfirm,
  message,
} from 'antd';
import { PlusOutlined, DeleteOutlined, ExpandAltOutlined, ShrinkOutlined } from '@ant-design/icons';
import { costCenterLabel } from '../../api';
import dayjs, { Dayjs } from 'dayjs';
import { listMRs, getLookups, deleteMR } from '../../api';
import type { MRListRow, MRListLine, MRListAttention } from '../../api';
import { useAuth } from '../../context/AuthContext';
import ConfigurableTable from '../../components/ConfigurableTable';
import type { ConfigurableColumn } from '../../components/ConfigurableTable';
import {
  DocCell,
  PersonCell,
  DateCell,
  StatusPill,
  UrgencyText,
  MoneyCell,
  MissingValue,
  ItemCell,
  VendorCell,
  FilterChips,
  DensityToggle,
  useDensity,
  MONO_FONT,
} from '../../components/table-cells';
import type { Chip } from '../../components/table-cells';
import { onPrefill, prefillLabel } from '../../utils/priceSource';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

/** Chip order on the list header. */
const MR_STATUS_ORDER = [
  'Draft',
  'Submitted',
  'Approved',
  'Partially Ordered',
  'Ordered',
  'Partially Received',
  'Received',
  'Rejected',
];

/** Statuses that are neither finished nor abandoned nor still a draft. */
const CLOSED_STATUSES = ['Received', 'Rejected', 'Draft'];

/** Sent when a status chip and an attention chip cannot both be satisfied — matches no MR. */
const NO_MATCH_STATUS = '__none__';

const VENDOR_SOURCE_LABEL: Record<string, string> = {
  requester: "requester's ask",
  department: "department's usual",
  procurement: 'chosen by procurement',
};

interface Department {
  id: number;
  name: string;
}

const dateRangeCodec = {
  serialize: (v: [Dayjs | null, Dayjs | null] | null) => (v ? [v[0] ? v[0].format('YYYY-MM-DD') : null, v[1] ? v[1].format('YYYY-MM-DD') : null] : null),
  deserialize: (raw: any): [Dayjs | null, Dayjs | null] | null => (raw ? [raw[0] ? dayjs(raw[0]) : null, raw[1] ? dayjs(raw[1]) : null] : null),
};

const fmtMoney = (v: any) => Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

/** "on estimate" when the actual matches, otherwise the signed percentage away from it. */
const varianceLabel = (actual: number, estimate: number): string => {
  if (Math.abs(actual - estimate) < 0.005) return 'on estimate';
  const pct = ((actual - estimate) / estimate) * 100;
  return `${pct > 0 ? '+' : '−'}${Math.abs(pct).toFixed(0)}%`;
};

/** A line typed by hand: no catalogue link and no code to show. */
const FreeTextMark: React.FC = () => (
  <span
    style={{
      display: 'inline-block',
      border: '1px dashed #cfd4dd',
      borderRadius: 4,
      padding: '0 5px',
      marginBottom: 3,
      font: `500 11px ${MONO_FONT}`,
      color: '#8a94a6',
    }}
  >
    free text
  </span>
);

/** Lines of one MR, shown under the row when expanded. */
const MRLinesTable: React.FC<{ lines: MRListLine[]; compact: boolean; showActual: boolean; showPrice: boolean }> = ({ lines, compact, showActual, showPrice }) => (
  <Table<MRListLine>
    size="small"
    pagination={false}
    rowKey="id"
    dataSource={lines || []}
    style={{ margin: '4px 0 4px 24px' }}
    columns={[
      { title: '#', dataIndex: 'lineNo', width: 40, align: 'center' as const },
      {
        title: 'Item',
        key: 'item',
        render: (_: any, l: MRListLine) => {
          const freeText = !l.catalogItem && !l.partNumber;
          return (
            <div>
              {freeText && <FreeTextMark />}
              <ItemCell
                partNumber={l.catalogItem?.partNumber ?? l.partNumber}
                description={l.catalogItem?.description ?? l.itemDescription}
                descriptionAr={l.catalogItem?.descriptionAr}
                linkTo={l.catalogItem?.id ? `/catalog/${l.catalogItem.id}` : undefined}
                compact={compact}
              />
            </div>
          );
        },
      },
      {
        title: 'Qty',
        key: 'qty',
        width: 100,
        align: 'right' as const,
        render: (_: any, l: MRListLine) => `${Number(l.qty)} ${l.unit?.name || ''}`.trim(),
      },
      // Price prefill (2026-09-29): requesters see no line prices on the list.
      ...(!showPrice ? [] : [{
        title: 'Price',
        key: 'price',
        width: 170,
        align: 'right' as const,
        render: (_: any, l: MRListLine) => {
          // Procurement sees what was actually paid (from the linked PO line) with the estimate under it;
          // requesters only ever see the estimate.
          const paid = showActual ? (l.poLines || []).find((pl) => pl.actualUnitPrice != null) : undefined;
          if (paid && paid.actualUnitPrice != null) {
            const est = l.unitPrice == null ? null : Number(l.unitPrice);
            const act = Number(paid.actualUnitPrice);
            const over = est != null && est > 0 && act > est;
            const pct = est != null && est > 0 ? Math.round(((act - est) / est) * 100) : null;
            const rule = (
              <span style={over ? { color: '#b42323' } : undefined}>
                est. {est == null ? '—' : est.toLocaleString('en-US')}
                {pct != null ? (pct === 0 ? ' · on estimate' : ` · ${pct > 0 ? '+' : ''}${pct}%`) : ''}
                {paid.poHeader ? ` · ${paid.poHeader.poId}` : ''}
              </span>
            );
            return <MoneyCell align="right" compact={compact} amount={act} currency={l.currency?.code} rule={rule} ruleTone="paid" />;
          }
          return (
            <MoneyCell
              align="right"
              compact={compact}
              amount={l.unitPrice == null ? null : Number(l.unitPrice)}
              currency={l.currency?.code}
              rule={onPrefill(l as any) ? prefillLabel(l as any) : l.origUnitPrice != null || l.refinedAt ? 'refined' : Number(l.unitPrice) > 0 ? 'requester est.' : null}
            />
          );
        },
      }]),
      {
        title: 'Vendor',
        key: 'vendor',
        width: 190,
        render: (_: any, l: MRListLine) => {
          const src = l.vendorSource ? VENDOR_SOURCE_LABEL[l.vendorSource] : null;
          return (
            <div style={{ lineHeight: 1.35 }}>
              <VendorCell name={l.vendor?.name} appCode={l.vendor?.appCode} />
              {src && !compact ? (
                <span style={{ display: 'block', fontSize: 11.5, color: '#8a94a6', marginTop: 1 }}>{src}</span>
              ) : null}
            </div>
          );
        },
      },
      {
        title: 'Approval',
        dataIndex: 'lineApprovalStatus',
        width: 110,
        render: (v: string) => <StatusPill status={v} />,
      },
      {
        title: 'Cost center',
        key: 'cc',
        width: 200,
        ellipsis: true,
        render: (_: any, l: MRListLine) =>
          l.costCenter ? costCenterLabel(l.costCenter) : <MissingValue label="no cost center" compact={compact} />,
      },
    ]}
  />
);

const MRList: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, isApprover, isProcurement, isRequester, canAccess, myDepartmentIds } = useAuth();
  const [loading, setLoading] = useState(true);
  const [mrs, setMrs] = useState<MRListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [attentionCounts, setAttentionCounts] = useState<MRListAttention>({
    submitted: 0,
    approvedNotOrdered: 0,
    neededByPassed: 0,
    lineCancellations: 0,
  });
  const [page, setPage] = usePersistedState('mrs', 'page', 1);
  const [pageSize, setPageSize] = usePersistedState('mrs', 'pageSize', 20);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [filterDepts, setFilterDepts] = usePersistedState<number[]>('mrs', 'depts', []);
  const [statusChip, setStatusChip] = usePersistedState<string | null>('mrs', 'statusChip', null);
  const [attention, setAttention] = usePersistedState<string[]>('mrs', 'attention', []);
  const [dateRange, setDateRange] = usePersistedState<[Dayjs | null, Dayjs | null] | null>('mrs', 'dateRange', null, dateRangeCodec);
  useScrollMemory('mrs', !loading);
  const [expandedKeys, setExpandedKeys] = usePersistedState<number[]>('mrs', 'expanded', []);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [density, setDensity] = useDensity('mr-list');
  const compact = density === 'compact';

  /** Requesters see their own estimate, never what procurement actually paid. */
  const canSeeActuals = !isRequester;
  /** Price prefill (2026-09-29): MR values and line prices are for procurement, approvers and admins only. */
  const seesPrices = isAdmin || isApprover || isProcurement;

  const handleDeleteMR = async (record: MRListRow) => {
    try {
      await deleteMR(record.id);
      message.success(`MR ${record.mrId} deleted`);
      fetchMRs();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to delete MR');
    }
  };

  const handleBulkDeleteMRs = async () => {
    if (selectedRowKeys.length === 0) return;
    setDeleting(true);
    let successCount = 0;
    const errors: string[] = [];
    for (const id of selectedRowKeys) {
      try {
        await deleteMR(id);
        successCount++;
      } catch (err: any) {
        errors.push(`MR #${id}: ${err.response?.data?.error || err.message}`);
      }
    }
    if (successCount > 0) message.success(`${successCount} MR(s) deleted`);
    if (errors.length > 0) message.error(`Failed: ${errors.join('; ')}`);
    setSelectedRowKeys([]);
    setDeleting(false);
    fetchMRs();
  };

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const lookups = await getLookups();
        setDepartments(lookups.departments);
      } catch (err) {
        console.error('Failed to load lookups', err);
      }
    };
    loadLookups();
  }, []);

  const fetchMRs = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: pageSize };
      if (filterDepts.length > 0) params.departmentIds = filterDepts.join(',');
      if (dateRange?.[0]) params.startDate = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1]) params.endDate = dateRange[1].format('YYYY-MM-DD');

      // Attention chips that are a status question are answered by the server;
      // "needed-by passed" is applied to the loaded rows below.
      const attStatuses: string[] = [];
      // Partially Approved MRs still have lines waiting for a decision.
      if (attention.includes('waiting')) attStatuses.push('Submitted', 'Partially Approved');
      if (attention.includes('approvedNotOrdered')) attStatuses.push('Approved', 'Partially Ordered');
      // Line cancellations waiting for the CEO: a server-side filter on the lines (2026-09-15).
      if (attention.includes('lineCancellations')) params.lineCancellations = '1';

      if (attStatuses.length > 0) {
        const wanted = Array.from(new Set(attStatuses));
        const effective = statusChip ? wanted.filter((s) => s === statusChip) : wanted;
        params.statuses = effective.length > 0 ? effective.join(',') : NO_MATCH_STATUS;
      } else if (statusChip) {
        params.status = statusChip;
      }

      const result = await listMRs(params);
      setMrs(result.data || []);
      setTotal(result.total || 0);
      setStatusCounts(result.statusCounts || {});
      if (result.attention) setAttentionCounts(result.attention);
    } catch (err) {
      console.error('Failed to load MRs', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filterDepts, statusChip, attention, dateRange]);

  useEffect(() => {
    fetchMRs();
  }, [fetchMRs]);

  /** "Needed-by passed" has no server filter — it narrows the page that was loaded. */
  const rows = useMemo(() => {
    if (!attention.includes('neededByPassed')) return mrs;
    const today = dayjs().startOf('day');
    return mrs.filter(
      (m) => m.neededBy && dayjs(m.neededBy).startOf('day').isBefore(today) && !CLOSED_STATUSES.includes(m.status),
    );
  }, [mrs, attention]);

  const scopeTotal = useMemo(
    () => Object.values(statusCounts).reduce((sum, n) => sum + n, 0) || total,
    [statusCounts, total],
  );

  const openCount = useMemo(
    () =>
      Object.entries(statusCounts).reduce(
        (sum, [status, n]) => (CLOSED_STATUSES.includes(status) ? sum : sum + n),
        0,
      ),
    [statusCounts],
  );

  const statusChips: Chip[] = MR_STATUS_ORDER.map((s) => ({ key: s, label: s, count: statusCounts[s] ?? 0 }));

  const attentionChips: Chip[] = [
    ...(isApprover
      ? [{ key: 'waiting', label: 'Waiting for me', tone: 'info' as const, count: attentionCounts.submitted }]
      : []),
    { key: 'approvedNotOrdered', label: 'Approved, not ordered', tone: 'warn', count: attentionCounts.approvedNotOrdered },
    { key: 'neededByPassed', label: 'Needed-by passed', tone: 'bad', count: attentionCounts.neededByPassed },
    ...(isApprover || isAdmin
      ? [{ key: 'lineCancellations', label: 'Line cancellations', tone: 'warn' as const, count: attentionCounts.lineCancellations ?? 0 }]
      : []),
  ];

  const columns: ConfigurableColumn<MRListRow>[] = [
    {
      title: 'MR',
      key: 'mrId',
      width: 170,
      hideable: false,
      render: (_: any, record: MRListRow) => (
        <DocCell
          id={record.mrId || `MR-${String(record.id).padStart(4, '0')}`}
          linkTo={`/mrs/${record.id}`}
          date={record.requestDate || record.createdAt}
          meta={record.urgency ? <UrgencyText urgency={record.urgency} /> : undefined}
          compact={compact}
        />
      ),
    },
    {
      title: 'Requested by',
      key: 'requestedBy',
      width: 170,
      render: (_: any, record: MRListRow) => (
        <PersonCell name={record.requestedBy?.displayName} sub={record.department?.name} compact={compact} />
      ),
    },
    {
      title: 'Needed by',
      key: 'neededBy',
      width: 130,
      render: (_: any, record: MRListRow) => (
        <DateCell
          value={record.neededBy}
          dueDate
          settled={record.status === 'Received' || record.status === 'Rejected'}
          compact={compact}
          missingLabel="no date"
        />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (val: string) => <StatusPill status={val} />,
    },
    {
      title: 'Value',
      key: 'value',
      width: 150,
      align: 'right' as const,
      render: (_: any, record: MRListRow) => {
        const currency = record.lines?.[0]?.currency?.code ?? undefined;
        const grossEstimate = record.mrTotal == null ? null : Number(record.mrTotal);
        // Net of cancelled (2026-09-22): the bold figure is what is still requested; the stored
        // total stays as requested and is named in the small print with the cancelled part.
        const cancelled = Number(record.cancelledTotal ?? 0);
        const estimate = record.netTotal != null ? Number(record.netTotal) : grossEstimate;
        const actual = record.netActualTotal != null
          ? Number(record.netActualTotal)
          : record.actualTotal == null ? null : Number(record.actualTotal);
        const cancelledText = cancelled > 0.005 && grossEstimate != null
          ? (
            <>
              {fmtMoney(grossEstimate)} requested ·{' '}
              <span style={{ color: '#b42323' }}>{(estimate ?? 0) <= 0.005 ? 'all cancelled' : `${fmtMoney(cancelled)} cancelled`}</span>
            </>
          )
          : null;

        if (canSeeActuals && actual != null && (estimate ?? 0) > 0.005) {
          const hasEstimate = estimate != null && estimate > 0;
          const ruleText = hasEstimate
            ? `est. ${fmtMoney(estimate)} · ${varianceLabel(actual, estimate as number)}`
            : 'no estimate';
          const over = hasEstimate && actual > (estimate as number) + 0.005;
          return (
            <MoneyCell
              align="right"
              compact={compact}
              amount={actual}
              currency={currency}
              rule={(
                <>
                  {over ? <span style={{ color: '#b42323' }}>{ruleText}</span> : ruleText}
                  {cancelledText ? <br /> : null}
                  {cancelledText}
                </>
              )}
            />
          );
        }

        if (estimate == null || (estimate === 0 && !cancelledText)) {
          return (
            <div style={{ textAlign: 'right' }}>
              <MissingValue label="no estimate" compact={compact} />
            </div>
          );
        }
        return (
          <MoneyCell align="right" compact={compact} amount={estimate} currency={currency} rule={cancelledText ?? 'estimate'} />
        );
      },
    },
    {
      title: 'Lines · POs',
      key: 'linesPos',
      width: 160,
      render: (_: any, record: MRListRow) => {
        const firstPo = (record.lines || [])
          .map((l) => l.poLines?.[0]?.poHeader)
          .find((h): h is { id: number; poId: string; status: string } => !!h);

        let poPart: React.ReactNode;
        if (firstPo) {
          poPart = (
            <Link to={`/pos/${firstPo.id}`} style={{ font: `500 12.5px ${MONO_FONT}` }}>
              {firstPo.poId}
            </Link>
          );
        } else if (record.firstPoId) {
          poPart = (
            <Link to={`/pos/${record.firstPoId}`} style={{ font: `500 12.5px ${MONO_FONT}` }}>
              {record.firstPoId}
            </Link>
          );
        } else if (record.status === 'Approved' || record.status === 'Partially Ordered') {
          poPart = <MissingValue label="not ordered" compact={compact} />;
        } else {
          poPart = <span style={{ color: '#8a94a6' }}>—</span>;
        }

        return (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {record.lineCount} · {poPart}
          </span>
        );
      },
    },
    ...(isAdmin
      ? [
          {
            title: 'Delete',
            key: 'delete',
            width: 80,
            render: (_: any, record: MRListRow) => (
              <Popconfirm
                title="Are you sure you want to delete this MR?"
                description="This action cannot be undone."
                onConfirm={() => handleDeleteMR(record)}
                okText="Yes, Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Material Requests
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {scopeTotal.toLocaleString('en-US')} total · {openCount.toLocaleString('en-US')} open
          </Text>
        </Col>
        <Col>
          <Space>
            <DensityToggle value={density} onChange={setDensity} />
            {canAccess('purchasing.mrs', 'create') && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/mrs/new')}
              >
                New MR
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          {(canAccess('purchasing.mrs', 'full') || myDepartmentIds.length > 1) && (
            <Select
              mode="multiple"
              maxTagCount="responsive"
              optionFilterProp="label"
              placeholder="Department"
              allowClear
              style={{ minWidth: 180 }}
              onChange={(val: number[]) => {
                setFilterDepts(val);
                setPage(1);
              }}
              value={filterDepts}
              options={departments
                .filter((d) => canAccess('purchasing.mrs', 'full') || myDepartmentIds.includes(d.id))
                .map((d) => ({ label: d.name, value: d.id }))}
            />
          )}
          <RangePicker
            onChange={(dates) => {
              setDateRange(dates as [Dayjs | null, Dayjs | null] | null);
              setPage(1);
            }}
            value={dateRange}
          />
          {expandedKeys.length > 0 && mrs.some((m) => expandedKeys.includes(m.id))
            ? <Button icon={<ShrinkOutlined />} onClick={() => setExpandedKeys([])}>Collapse all</Button>
            : <Button icon={<ExpandAltOutlined />} onClick={() => setExpandedKeys(mrs.map((m) => m.id))}>Expand all</Button>}
        </Space>
      </Card>

      <FilterChips
        style={{ marginBottom: 12 }}
        chips={statusChips}
        value={statusChip}
        onChange={(key) => {
          setStatusChip(key);
          setPage(1);
        }}
        allCount={scopeTotal}
        attention={attentionChips}
        attentionValue={attention}
        onAttentionChange={(keys) => {
          setAttention(keys);
          setPage(1);
        }}
      />

      {isAdmin && selectedRowKeys.length > 0 && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space>
            <Typography.Text strong>{selectedRowKeys.length} MR(s) selected</Typography.Text>
            <Popconfirm
              title={`Are you sure you want to delete ${selectedRowKeys.length} MR(s)?`}
              description="This action cannot be undone."
              onConfirm={handleBulkDeleteMRs}
              okText="Yes, Delete All"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
            >
              <Button danger icon={<DeleteOutlined />} loading={deleting}>
                Delete Selected
              </Button>
            </Popconfirm>
            <Button onClick={() => setSelectedRowKeys([])}>Clear Selection</Button>
          </Space>
        </Card>
      )}

      <ConfigurableTable<MRListRow>
        tableId="mr-list"
        columns={seesPrices ? columns : columns.filter((c) => c.key !== 'value')}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        expandable={{
          expandedRowKeys: expandedKeys,
          onExpandedRowsChange: (keys) => setExpandedKeys(keys as number[]),
          expandedRowRender: (record: MRListRow) => <MRLinesTable lines={record.lines} compact={compact} showActual={canSeeActuals} showPrice={seesPrices} />,
          rowExpandable: (record: MRListRow) => (record.lines?.length ?? record.lineCount ?? 0) > 0,
        }}
        rowSelection={isAdmin ? {
          selectedRowKeys,
          onChange: (keys: React.Key[]) => setSelectedRowKeys(keys as number[]),
        } : undefined}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
          showSizeChanger: true,
          showTotal: (t) => `Total ${t} records`,
        }}
        size={compact ? 'small' : 'middle'}
        scroll={{ x: 1000 }}
      />
    </div>
  );
};

export default MRList;
